import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    Space,
    Typography,
    Tag,
    Modal,
    message,
    Popconfirm,
    Row,
    Col,
    Alert,
    Spin
} from 'antd';
import {
    CreditCardOutlined,
    PlusOutlined,
    DeleteOutlined,
    StarOutlined,
    StarFilled,
    LockOutlined
} from '@ant-design/icons';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../../hooks/useAuth';
import {
    useCreateSetupIntentMutation,
    useAttachPaymentMethodMutation,
    useDetachPaymentMethodMutation,
    useSetDefaultPaymentMethodMutation,
} from '../../services/nestjs-base-api';
import { IPaymentMethod } from '../../services/nestjs-base-api/types';

const { Title, Text } = Typography;

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY && 
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY !== 'pk_test_your_stripe_publishable_key_here' 
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) 
    : null;

interface IPaymentMethodsProps {
    paymentMethods: IPaymentMethod[];
}

// Helper to get browser's country from locale
function getBrowserCountry(): string {
    try {
        const locale = navigator.language || (navigator as any).userLanguage;
        if (locale && locale.includes('-')) {
            return locale.split('-')[1].toUpperCase();
        }
        const localeCountryMap: Record<string, string> = {
            'en': 'US',
            'de': 'DE',
            'fr': 'FR',
            'es': 'ES',
            'it': 'IT',
            'pt': 'BR',
            'ja': 'JP',
            'ko': 'KR',
            'zh': 'CN',
        };
        const langCode = locale?.split('-')[0]?.toLowerCase();
        return localeCountryMap[langCode] || 'US';
    } catch {
        return 'US';
    }
}

const AddPaymentMethodForm: React.FunctionComponent<{
    onSuccess: () => void;
    onCancel: () => void;
}> = ({ onSuccess, onCancel }) => {
    const stripe = useStripe();
    const elements = useElements();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [attachPaymentMethod] = useAttachPaymentMethodMutation();

    const handleSubmit = async () => {
        if (!stripe || !elements) {
            return;
        }

        setLoading(true);
        setErrorMessage(null);

        try {
            // Confirm the setup intent and get the payment method
            const { error, setupIntent } = await stripe.confirmSetup({
                elements,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: 'if_required',
            });

            if (error) {
                setErrorMessage(error.message || 'Failed to add payment method');
                return;
            }

            if (setupIntent?.status === 'succeeded' && setupIntent.payment_method) {
                const paymentMethodId = typeof setupIntent.payment_method === 'string'
                    ? setupIntent.payment_method
                    : setupIntent.payment_method.id;

                // Attach the payment method to the organization
                await attachPaymentMethod({
                    jwtToken: user!.jwtToken,
                    organisationId: user!.organisation.id,
                    paymentMethodId,
                }).unwrap();

                message.success('Payment method added successfully');
                onSuccess();
            }
        } catch (error: any) {
            console.error('Error adding payment method:', error);
            setErrorMessage(error?.data?.message || 'Failed to add payment method');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <PaymentElement
                    options={{
                        layout: 'tabs',
                    }}
                />
            </div>

            {errorMessage && (
                <Alert
                    message={errorMessage}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <div style={{ marginTop: 8, marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <LockOutlined style={{ marginRight: 4 }} />
                    Your payment information is securely processed by Stripe. We do not store your card details.
                </Text>
            </div>

            <div style={{ textAlign: 'right' }}>
                <Space>
                    <Button onClick={onCancel} disabled={loading}>Cancel</Button>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={loading}
                        disabled={!stripe || !elements}
                        icon={<LockOutlined />}
                    >
                        Add Payment Method
                    </Button>
                </Space>
            </div>
        </div>
    );
};

const PaymentMethods: React.FunctionComponent<IPaymentMethodsProps> = ({ paymentMethods }) => {
    const { user } = useAuth();
    const [showAddModal, setShowAddModal] = useState(false);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [setupIntentLoading, setSetupIntentLoading] = useState(false);
    const [setupIntentError, setSetupIntentError] = useState<string | null>(null);

    const isStripeConfigured = stripePromise !== null;

    const [createSetupIntent] = useCreateSetupIntentMutation();
    const [detachPaymentMethod, { isLoading: detachLoading }] = useDetachPaymentMethodMutation();
    const [setDefaultPaymentMethod, { isLoading: defaultLoading }] = useSetDefaultPaymentMethodMutation();

    // Create setup intent when modal opens
    useEffect(() => {
        const initSetupIntent = async () => {
            if (showAddModal && !clientSecret && user?.jwtToken && user?.organisation?.id) {
                setSetupIntentLoading(true);
                setSetupIntentError(null);
                try {
                    const result = await createSetupIntent({
                        jwtToken: user.jwtToken,
                        organisationId: user.organisation.id,
                        billingCountry: getBrowserCountry(),
                    }).unwrap();
                    setClientSecret(result.clientSecret);
                } catch (err: any) {
                    console.error('Failed to create setup intent:', err);
                    setSetupIntentError(err?.data?.message || 'Failed to initialize payment form');
                } finally {
                    setSetupIntentLoading(false);
                }
            }
        };
        initSetupIntent();
    }, [showAddModal, clientSecret, user, createSetupIntent]);

    const handleCloseModal = () => {
        setShowAddModal(false);
        setClientSecret(null);
        setSetupIntentError(null);
    };

    const elementsOptions: StripeElementsOptions = {
        clientSecret: clientSecret || undefined,
        appearance: {
            theme: 'stripe',
            variables: {
                colorPrimary: '#F59E0B',
            },
        },
    };

    const handleDetachPaymentMethod = async (paymentMethodId: string) => {
        try {
            await detachPaymentMethod({
                jwtToken: user!.jwtToken,
                organisationId: user!.organisation.id,
                paymentMethodId,
            }).unwrap();
            message.success('Payment method removed successfully');
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to remove payment method');
        }
    };

    const handleSetDefault = async (paymentMethodId: string) => {
        try {
            await setDefaultPaymentMethod({
                jwtToken: user!.jwtToken,
                organisationId: user!.organisation.id,
                paymentMethodId,
            }).unwrap();
            message.success('Default payment method updated');
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to update default payment method');
        }
    };

    if (!isStripeConfigured) {
        return (
            <div>
                <Title level={4}>Payment Methods</Title>
                <Alert
                    message="Stripe Not Configured"
                    description="Payment method management is not available because Stripe is not properly configured. Please contact your administrator."
                    type="warning"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0 }}>Payment Methods</Title>
                <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={() => setShowAddModal(true)}
                >
                    Add Payment Method
                </Button>
            </div>

            {paymentMethods.length === 0 ? (
                <Card>
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <CreditCardOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                        <Title level={4} type="secondary">No Payment Methods</Title>
                        <Text type="secondary">
                            Add a payment method to subscribe to our plans and manage your billing automatically.
                        </Text>
                        <div style={{ marginTop: 16 }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setShowAddModal(true)}
                            >
                                Add Your First Payment Method
                            </Button>
                        </div>
                    </div>
                </Card>
            ) : (
                <Row gutter={[16, 16]}>
                    {paymentMethods.map((pm) => {
                        const brandColors: Record<string, { bg: string; gradient: string }> = {
                            visa: { bg: '#1a1f71', gradient: 'linear-gradient(135deg, #1a1f71 0%, #2d3494 100%)' },
                            mastercard: { bg: '#eb001b', gradient: 'linear-gradient(135deg, #eb001b 0%, #f79e1b 100%)' },
                            amex: { bg: '#006fcf', gradient: 'linear-gradient(135deg, #006fcf 0%, #00aeef 100%)' },
                            discover: { bg: '#ff6000', gradient: 'linear-gradient(135deg, #ff6000 0%, #ffaa00 100%)' },
                            default: { bg: '#434343', gradient: 'linear-gradient(135deg, #434343 0%, #636363 100%)' },
                        };
                        const colors = brandColors[pm.cardBrand?.toLowerCase()] || brandColors.default;

                        return (
                            <Col xs={24} sm={12} lg={8} key={pm.id}>
                                <Card
                                    style={{
                                        borderRadius: 12,
                                        overflow: 'hidden',
                                        border: pm.isDefault ? '2px solid #faad14' : '1px solid #f0f0f0',
                                    }}
                                    styles={{ body: { padding: 0 } }}
                                >
                                    {/* Card Header - Credit Card Style */}
                                    <div style={{
                                        background: colors.gradient,
                                        padding: '20px 20px 16px',
                                        color: 'white',
                                        position: 'relative',
                                    }}>
                                        {pm.isDefault && (
                                            <Tag
                                                color="gold"
                                                style={{
                                                    position: 'absolute',
                                                    top: 12,
                                                    right: 12,
                                                    margin: 0,
                                                }}
                                            >
                                                <StarFilled /> Default
                                            </Tag>
                                        )}
                                        <div style={{ marginBottom: 20, opacity: 0.9 }}>
                                            <CreditCardOutlined style={{ fontSize: 28 }} />
                                        </div>
                                        <div style={{
                                            fontSize: 20,
                                            fontFamily: 'monospace',
                                            letterSpacing: 2,
                                            marginBottom: 12,
                                        }}>
                                            •••• •••• •••• {pm.cardLast4}
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-end',
                                        }}>
                                            <div>
                                                <div style={{ fontSize: 10, opacity: 0.7, textTransform: 'uppercase' }}>
                                                    Expires
                                                </div>
                                                <div style={{ fontSize: 14 }}>
                                                    {pm.cardExpMonth?.toString().padStart(2, '0')}/{pm.cardExpYear?.toString().slice(-2)}
                                                </div>
                                            </div>
                                            <div style={{
                                                fontSize: 18,
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                letterSpacing: 1,
                                            }}>
                                                {pm.cardBrand}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Card Footer - Actions */}
                                    <div style={{
                                        padding: '12px 16px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        background: '#fafafa',
                                    }}>
                                        {!pm.isDefault ? (
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<StarOutlined />}
                                                onClick={() => handleSetDefault(pm.stripePaymentMethodId)}
                                                loading={defaultLoading}
                                                style={{ paddingLeft: 0 }}
                                            >
                                                Set as default
                                            </Button>
                                        ) : (
                                            <span />
                                        )}
                                        <Popconfirm
                                            title="Remove payment method"
                                            description="Are you sure you want to remove this payment method?"
                                            onConfirm={() => handleDetachPaymentMethod(pm.stripePaymentMethodId)}
                                            okText="Remove"
                                            cancelText="Cancel"
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                loading={detachLoading}
                                                danger
                                            >
                                                Remove
                                            </Button>
                                        </Popconfirm>
                                    </div>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}

            {isStripeConfigured && (
                <Modal
                    title="Add Payment Method"
                    open={showAddModal}
                    onCancel={handleCloseModal}
                    footer={null}
                    width={500}
                    destroyOnClose
                >
                    {setupIntentLoading ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <Spin />
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary">Loading payment form...</Text>
                            </div>
                        </div>
                    ) : setupIntentError ? (
                        <Alert
                            type="error"
                            showIcon
                            message="Failed to initialize payment"
                            description={setupIntentError}
                        />
                    ) : clientSecret ? (
                        <Elements stripe={stripePromise} options={elementsOptions}>
                            <AddPaymentMethodForm
                                onSuccess={handleCloseModal}
                                onCancel={handleCloseModal}
                            />
                        </Elements>
                    ) : (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <Spin />
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary">Initializing...</Text>
                            </div>
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
};

export default PaymentMethods;